import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { FiX } from "react-icons/fi";
import { RootState } from "../../../store";
import { ChatMessage } from "../../../store/aiChatSlice";
import MessageWithChart from "../../../components/DataVisualization/MessageWithChart";
import { parseUpdateBlocks } from "../../../utils/chatUpdates";
import ProfilePictureRounded from "../../ProfilePictureRounded";
import { ReactComponent as ErodeLogo } from "../../../assets/images/erode-logo-black.svg";
import { stripAssetContext } from "../../../utils/assetContext";

// Full-screen modal that expands a single chat bubble for easier reading.

interface ChatBubbleModalProps {
  message: ChatMessage | null;
  userName: string;
  userProfileUrl?: string;
  triggerText?: string;
  conversationId?: string;
  onClose: () => void;
}

const ChatBubbleModal: React.FC<ChatBubbleModalProps> = ({
  message,
  userName,
  userProfileUrl,
  triggerText,
  conversationId,
  onClose,
}) => {
  // A global modal (e.g. the lightbox) opened from inside this one owns Escape until it closes
  const globalModalOpen = useSelector((state: RootState) => state.modalState.modalOpen);

  // Close on Escape key
  useEffect(() => {
    if (!message) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !globalModalOpen) onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [message, onClose, globalModalOpen]);

  // Lock background scroll while open — kept separate so re-binding Escape never churns the lock
  useEffect(() => {
    if (!message) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [message]);

  if (!message) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Expanded message"
        className="eai-card relative flex h-[92dvh] w-[95vw] max-w-6xl flex-col"
      >
        {/* Header — ModalBase's gutter and rule, with the author in place of a title */}
        <div className="eai-modal-gutter flex items-center justify-between border-b border-eai-border py-4">
          <div className="flex items-center gap-2">
            {message.isUser ? (
              <>
                <ProfilePictureRounded
                  diameter={24}
                  pictureURL={userProfileUrl}
                  className="border-white"
                  onClick={() => {}}
                />
                <div className="eai-card-title">
                  {userName}
                </div>
              </>
            ) : (
              <ErodeLogo className="h-4 w-auto" aria-label="Erode" />
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="eai-btn-icon border-transparent bg-transparent"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="scrollbar-hover eai-modal-gutter flex-1 overflow-y-auto whitespace-pre-wrap break-words py-5 font-inter text-eai-body-sm text-eai-ink">
          {message.isUser ? (
            stripAssetContext(message.text)
          ) : (
            // Strip the streamed <update> progress steps — the expanded view shows only the final answer.
            <MessageWithChart
              message={parseUpdateBlocks(message.text).response}
              triggerText={triggerText}
              conversationId={conversationId}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatBubbleModal;
